import { useQueryClient } from "react-query";

import useQuery from "hooks/useQuery";
import useMutation from "hooks/useMutation";

// La HCE decide si una feature de IA está bloqueada por T&C con
// GET /plans/team/:id/access (cacheado 30 s): sin invalidarlo, después de
// aceptar la feature seguía bloqueada y volvía a pedir los T&C hasta recargar.
const isPlanAccessQuery = (query) => {
  const key = String(query.queryKey?.[0] || "");
  return key.startsWith("plans/team/") && key.endsWith("/access");
};

// Estado de aceptación de T&C (GET /terms/status) + acción de aceptar.
// Fail-open: si no hay versión vigente, o la api falla, `current` viene null
// y no se muestra ni bloquea nada.
export default function useTerms() {
  const query = useQuery("terms/status");
  const status = query.data;
  const current = status?.current || null;
  const version = current?.version || null;

  const queryClient = useQueryClient();

  const [acceptMutation, acceptRest] = useMutation("terms/accept", {
    onSuccess: () => {
      query.refresh({ ...status, accepted: true, acceptedVersion: version, acceptedAt: Date.now() });
      queryClient.invalidateQueries({ predicate: isPlanAccessQuery });
    },
    // Si la versión cambió entre que se mostró el CTA y el click, la api
    // responde 409 terms_out_of_date; re-sincronizamos el status para que
    // muestre la versión nueva.
    onError: () => query.refetch(),
  });

  const needsAcceptance = !!current && status?.accepted === false;

  return {
    current,
    needsAcceptance,
    // CTA del dropdown de cuenta: SIEMPRE que la aceptación esté pendiente.
    // Ya no depende de un snooze/localStorage (antes sólo aparecía tras "Más
    // tarde"): al no haber banner, este es el único punto de entrada para
    // aceptar, así que debe estar visible mientras no se acepte.
    showNavCta: needsAcceptance,
    isAccepting: acceptRest.isLoading,
    accept: () => acceptMutation({ version, source: "drmanager-web" }),
  };
}

// Gating de una funcionalidad: true = bloqueada hasta aceptar los T&C vigentes.
// QUÉ features se gatean lo define el CÓDIGO (los call sites que llaman a este
// hook), no un array por versión: así, al publicar un T&C nuevo, se re-bloquean
// solas todas las features gateadas, sin tener que re-cargar ninguna lista (y
// sin el riesgo de que se desbloqueen por olvido). `featureKey` queda solo para
// documentar el call site.
// eslint-disable-next-line no-unused-vars
export function useIsFeatureLockedByTerms(featureKey) {
  const status = useQuery("terms/status").data;
  return !!status?.current && status?.accepted === false;
}
